import React from 'react';
import { styled, TextField as _TextField, Typography, IconButton } from '@mui/material';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';

// ***** Standard TextField ***** //

export function TextField(props) {
    return (
        <SdTextField
            sx={{ mt: 1 }}
            fullWidth
            variant="outlined"
            inputProps={props.textAlign ? { style: { textAlign: props.textAlign } } : undefined}
            {...props}
        />
    );
}

const SdTextField = _TextField;

// ***** Multi-Line TextArea w/ character counter ***** //

export function TextArea(props) {
    const { value } = props;

    const maxLength = props.maxLength || 1000;
    const error = props.error || value.length > maxLength;

    return (
        <>
            <SdTextArea
                multiline
                fullWidth
                variant="outlined"
                minRows={5}
                maxRows={5}
                error={error}
                {...props}
            />
            {value.length > maxLength ? (
                <CounterError>
                    {String(value.length)} of {maxLength}
                </CounterError>
            ) : (
                <Counter>
                    {String(value.length)} of {maxLength}
                </Counter>
            )}
        </>
    );
}

const SdTextArea = styled(_TextField)(({ theme }) => ({
    '& .MuiInputBase-inputMultiline': {
        resize: 'vertical',
    },
}));

const Counter = styled(Typography)(({ theme }) => ({
    ...theme.typography.caption,
    textAlign: 'right',
    marginTop: theme.spacing(1),
    color: theme.palette.grey[700],
}));

const CounterError = styled(Typography)(({ theme }) => ({
    ...theme.typography.caption,
    textAlign: 'right',
    marginTop: theme.spacing(1),
    color: theme.palette.error.main,
}));

// ***** Dropdown Selector ***** //

export function Dropdown(props) {
    return (
        <SdDropdown
            sx={{ mt: 1 }}
            fullWidth
            variant="outlined"
            select
            SelectProps={{ native: true }}
            inputProps={props.textAlign ? { style: { textAlign: props.textAlign } } : undefined}
            {...props}
        />
    );
}

const SdDropdown = _TextField;

// ***** Province Selector ***** //

export function ProvinceSelector(props) {
    switch (props.region) {
        case 'CA':
        case 'Canada':
            return (
                <Dropdown label="Province" {...props}>
                    <option value=""></option>
                    <option value="AB">Alberta</option>
                    <option value="BC">British Columbia</option>
                    <option value="MB">Manitoba</option>
                    <option value="NB">New Brunswick</option>
                    <option value="NL">Newfoundland and Labrador</option>
                    <option value="NS">Nova Scotia</option>
                    <option value="NU">Nunavut</option>
                    <option value="NT">Northwest Territories</option>
                    <option value="ON">Ontario</option>
                    <option value="PE">Prince Edward Island</option>
                    <option value="QC">Quebec</option>
                    <option value="SK">Saskatchewan</option>
                    <option value="YT">Yukon</option>
                </Dropdown>
            );

        case 'US':
        case 'United States':
            return (
                <Dropdown label="State" {...props}>
                    <option value=""></option>
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    <option value="AR">Arkansas</option>
                    <option value="CA">California</option>
                    <option value="CO">Colorado</option>
                    <option value="CT">Connecticut</option>
                    <option value="DE">Delaware</option>
                    <option value="DC">District Of Columbia</option>
                    <option value="FL">Florida</option>
                    <option value="GA">Georgia</option>
                    <option value="HI">Hawaii</option>
                    <option value="ID">Idaho</option>
                    <option value="IL">Illinois</option>
                    <option value="IN">Indiana</option>
                    <option value="IA">Iowa</option>
                    <option value="KS">Kansas</option>
                    <option value="KY">Kentucky</option>
                    <option value="LA">Louisiana</option>
                    <option value="ME">Maine</option>
                    <option value="MD">Maryland</option>
                    <option value="MA">Massachusetts</option>
                    <option value="MI">Michigan</option>
                    <option value="MN">Minnesota</option>
                    <option value="MS">Mississippi</option>
                    <option value="MO">Missouri</option>
                    <option value="MT">Montana</option>
                    <option value="NE">Nebraska</option>
                    <option value="NV">Nevada</option>
                    <option value="NH">New Hampshire</option>
                    <option value="NJ">New Jersey</option>
                    <option value="NM">New Mexico</option>
                    <option value="NY">New York</option>
                    <option value="NC">North Carolina</option>
                    <option value="ND">North Dakota</option>
                    <option value="OH">Ohio</option>
                    <option value="OK">Oklahoma</option>
                    <option value="OR">Oregon</option>
                    <option value="PA">Pennsylvania</option>
                    <option value="RI">Rhode Island</option>
                    <option value="SC">South Carolina</option>
                    <option value="SD">South Dakota</option>
                    <option value="TN">Tennessee</option>
                    <option value="TX">Texas</option>
                    <option value="UT">Utah</option>
                    <option value="VT">Vermont</option>
                    <option value="VA">Virginia</option>
                    <option value="WA">Washington</option>
                    <option value="WV">West Virginia</option>
                    <option value="WI">Wisconsin</option>
                    <option value="WY">Wyoming</option>
                    <option value="PR">Puerto Rico</option>
                    <option value="VI">Virgin Islands</option>
                    <option value="MP">Northern Mariana Islands</option>
                    <option value="GU">Guam</option>
                    <option value="AS">American Samoa</option>
                    <option value="PW">Palau</option>
                </Dropdown>
            );
        default:
            return null;
    }
}

// ***** Search Bar ***** //

export function SearchBar(props) {
    const { value, onChange, placeholder, autoFocus, onKeyPress } = props;

    return (
        <TextField
            value={value}
            onChange={(e) => onChange(e.target.value)}
            variant="outlined"
            size="small"
            placeholder={placeholder ?? 'Search...'}
            onKeyPress={onKeyPress}
            sx={{ marginTop: 0 }}
            InputProps={{
                autoFocus: autoFocus ?? false,
                startAdornment: <SearchIcon fontSize="small" />,
                endAdornment: (
                    <IconButton
                        size="small"
                        style={{ visibility: value ? 'visible' : 'hidden' }}
                        onClick={() => onChange('')}
                    >
                        <ClearIcon fontSize="small" />
                    </IconButton>
                ),
            }}
        />
    );
}

// ***** Date Picker ***** //

export function DatePicker(props) {
    return (
        <MuiDatePicker
            id={props.id}
            label={props.label}
            value={props.value}
            onChange={props.onChange}
            disabled={props.disabled}
            inputFormat={props.inputFormat || 'MM/DD/YYYY'}
            views={props.views}
            renderInput={(params) => (
                <TextField
                    fullWidth
                    {...params}
                    {...props.inputProps}
                    helperText={props.helperText}
                    error={props.error}
                />
            )}
            {...props.datePickerProps}
        />
    );
}

// ***** Time Picker ***** //

export function TimePicker(props) {
    return (
        <MuiTimePicker
            id={props.id}
            label={props.label}
            value={props.value}
            onChange={props.onChange}
            disabled={props.disabled}
            inputFormat={props.inputFormat || 'hh:mm A'}
            views={props.views || ['hours', 'minutes']}
            renderInput={(params) => (
                <TextField
                    fullWidth
                    {...params}
                    {...props.inputProps}
                    error={props.error}
                    helperText={props.helperText}
                />
            )}
            {...props.timePickerProps}
        />
    );
}
